<template>
  <v-data-table
    :items="invoices"
    :headers="headers"
    :loading="loading"
    :loading-text="t('billing.invoices.loading')"
    :mobile="tableMobile"
    items-per-page="-1"
    disable-sort
    hide-default-footer
  >
    <template #top>
      <div class="w-100 d-flex flex-column flex-sm-row ga-1 ga-sm-3">
        <div class="flex-1 flex-shrink-0">
          <VueDatePicker
            v-model="year"
            year-picker
            :year-range="[2018, new Date().getFullYear()]"
            :placeholder="t('billing.invoices.selectPeriod')"
            :locale="locale"
            :cancel-text="t('form.cancel')"
            :select-text="t('form.select')"
          />
        </div>
        <v-text-field
          v-model.trim="invoiceNumber"
          clearable
          :label="t('billing.invoices.invoiceNumber')"
          :placeholder="t('billing.invoices.searchInvoice')"
          class="flex-grow-1"
        />
      </div>
    </template>

    <template #item.status="{ item }">
      <v-chip class="text-capitalize" :color="statusChipColor(item.status)" label>
        {{ t(`billing.invoices.status.${item.status.toLowerCase()}`) }}
      </v-chip>
    </template>
    <template #item.billingPeriod="{ item }">
      {{ d(new Date(item.billingPeriod), { month: "long", year: "numeric" }) }}
    </template>
    <template #item.amount="{ item }">
      {{ displayAmount(item.amount) }}
    </template>
    <template #item.dueDate="{ item }">
      {{ item.dueDate ? format(item.dueDate, "dd/MM/yyyy") : "" }}
    </template>
    <template #item.invoiceNumber="{ item }">
      <kbd>{{ item.invoiceNumber }}</kbd>
    </template>
    <template #item.actions="{ item }">
      <div class="text-right">
        <v-icon
          v-if="item.invoiceNumber"
          size="18"
          color="secondary"
          @click="
            getDownloadOptions(
              item.invoiceNumber,
              d(new Date(item.billingPeriod), { month: 'long', year: 'numeric' }),
            )
          "
        >
          mdi-download
        </v-icon>
        <v-icon v-else size="18" color="grey"> mdi-download </v-icon>
      </div>
    </template>
  </v-data-table>
  <DownloadInvoiceOptions
    :show-download-options="showDownloadOptions"
    :files-available="filesAvailable"
    :header-text="downloadHeaderText"
    @update:model-value="showDownloadOptions = $event.value"
  />
</template>

<script setup lang="ts">
import {
  DownloadInvoice200ResponseInner,
  ListInvoicesV2200ResponseItemsInner,
  ListInvoicesV2200ResponseItemsInnerStatusEnum,
} from "@tableyeti/merchant-service";
import { format } from "date-fns";
import { ref, computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";
import { useYetipayApi } from "@/composables/useYetipayApi";
import { useApplicationStore } from "@/store/application";
import { displayAmount } from "@/utils/format";
const { t, locale, d } = useI18n();
const { width } = useDisplay();

const { api, currentMerchantId } = useYetipayApi();

const applicationStore = useApplicationStore();
const invoices = ref<ListInvoicesV2200ResponseItemsInner[] | undefined>([]);
const currentPage = ref(1);
const year = ref<number>(new Date().getFullYear());
const invoiceNumber = ref<string | undefined>();
const loading = ref(false);
const showDownloadOptions = ref(false);
const downloadHeaderText = ref("");
const filesAvailable = ref<DownloadInvoice200ResponseInner[]>([]);
const headers = [
  { title: "", value: "status" },
  { title: t("billing.invoices.period"), value: "billingPeriod" },
  { title: t("billing.invoices.amount"), value: "amount" },
  { title: t("billing.invoices.dueDate"), value: "dueDate" },
  { title: t("billing.invoices.invoiceNumberLabel"), value: "invoiceNumber" },
  { title: "", key: "actions" },
];
const tableMobile = computed(() => width.value <= 643);

const fetch = async () => {
  loading.value = true;
  const yearParam = year.value;
  const invoiceNumberParam = !invoiceNumber.value ? undefined : invoiceNumber.value;
  if (invoiceNumberParam && !/[A-Z0-9]{8}-([0-9]{4}|DRAFT)/.exec(invoiceNumberParam)) {
    loading.value = false;
    invoices.value = [];
    return;
  }
  try {
    const result = await api
      .listInvoicesV2(currentMerchantId, yearParam.toString(), invoiceNumberParam)
      .then((res) => res.data);
    invoices.value = result.items;
  } finally {
    loading.value = false;
  }
};

const getDownloadOptions = async (invoiceNumber: string, period: string) => {
  filesAvailable.value = [];
  showDownloadOptions.value = false;
  downloadHeaderText.value = period;
  try {
    filesAvailable.value = await api
      .downloadInvoice(currentMerchantId, invoiceNumber)
      .then((res) => res.data);
    showDownloadOptions.value = true;
  } catch (error) {
    console.error(error);
    applicationStore.notifyUser({
      message: t("billing.invoices.downloadError"),
      type: "error",
    });
  }
};

onMounted(fetch);
watch(() => ({ currentPage, year, invoiceNumber }), fetch, { deep: true });

const statusChipColor = (status: ListInvoicesV2200ResponseItemsInnerStatusEnum) => {
  switch (status) {
    case "paid":
      return "success";
    case "open":
      return "warning";
    case "overdue":
      return "error";
    default:
      return "grey";
  }
};
</script>
